import {useI18n} from '@yandex-delivery/core/i18n'
import {ServerError} from '@yandex-delivery/core/utils/network'
import {useQuery} from '@tanstack/react-query'
import {type PaymentCard} from '@yandex-delivery/api/model/payment'
import {useController, useFormContext} from 'react-hook-form'
import {useMemo} from 'react'

import {PaymentCardErrorCode, usePaymentCardService} from '../../../../../../../api/payment'
import {useDrawer} from '../../../../../components/Drawer/hooks/useDrawer'
import {ChangePaymentCardModalProps} from '../../ChangePaymentCardModal/types'
import {PaymentCardButtonProps} from '../../PaymentCardButton'
import type {PaymentCardProps} from '../types'

export type UsePaymentCardReturn = {
  baseProps: PaymentCardButtonProps
  changePaymentCardModalProps: ChangePaymentCardModalProps
}

export function usePaymentCard({getBody: externalGetBody, name, title}: PaymentCardProps): UsePaymentCardReturn {
  const i18n = useI18n()
  const {close, open, opened} = useDrawer()
  const {control, getValues, watch} = useFormContext()
  const {fetchPaymentCard} = usePaymentCardService()
  const {field, fieldState} = useController({
    name,
    control,
  })
  const paymentCard: PaymentCard | undefined = watch(name)

  const changeHandler = (nextPaymentCard: PaymentCard): void => {
    if (paymentCard && paymentCard.id === nextPaymentCard.id) {
      return
    }

    field.onChange(nextPaymentCard)
  }

  const {isPending, error} = useQuery<PaymentCard | undefined, ServerError>({
    queryKey: [`fetch/order-form/cards/${name}`],
    queryFn: () => fetchPaymentCard({onSuccess: changeHandler}),
  })

  const baseProps = useMemo<Omit<PaymentCardButtonProps, 'trail'>>(
    () => ({
      errorText: fieldState.invalid ? i18n('bdui.error.empty-payment-card') : undefined,
      isPending,
      onPress: open,
      paymentCard,
      title,
      cardErrorCode:
        error && error.status === 401
          ? // Не совсем корректная типизация `ServerError` (нет поля под код ошибки, поэтому значение передаётся в message)
            // Но известно, что в error.message передаётся `CardErrorCode`
            (error.message as PaymentCardErrorCode)
          : undefined,
    }),
    [error, fieldState.invalid, i18n, isPending, open, paymentCard, title],
  )

  return {
    baseProps,
    changePaymentCardModalProps: {
      paymentCard,
      getBody: () => externalGetBody(getValues()),
      onChange: changeHandler,
      onClose: close,
      opened,
    },
  }
}
